import { Icon } from '@zoom/components';
import React, { useState } from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useAuth } from '../../../app/AuthorizationProvider';
import { useConfig } from '../../../app/ConfigurationProvider';
import {
  DropdownItem,
  IUncontrolledDropdown,
  UncontrolledDropdown,
} from '../../../components/Dropdown/UncontrolledDropdown';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { compose } from '../../../utils/compose';
import { isUnauthorized } from '../../../utils/customFetch';
import { createEndOfDateRange } from '../../../utils/dateRange';
import { exportSchedule, downloadBlobFile } from '../../../utils/exportSchedule';
import { EXPORT_SCHEDULE_DROPDOWN_TOGGLE, EXPORT_SCHEDULE_FULL, EXPORT_SCHEDULE_ONE_DAY } from '../constants';

const exportTypes = {
  oneDay: 'oneDay',
  fullSchedule: 'fullSchedule',
};

export interface ExportScheduleType extends WithTranslation {
  currentDate: string;
  scheduleId: string;
  timezone: string;
}

export const ExportScheduleComponent: React.FC<ExportScheduleType> = ({ currentDate, scheduleId, timezone, t }) => {
  const auth = useAuth();
  const { restPath, tenant } = useConfig();
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const onClickHandler = (type: string, oneDayExport = false) => async () => {
    setDownloadInProgress(true);
    let endDateTime;
    let startDateTime;

    if (oneDayExport) {
      const date = new Date(currentDate);
      startDateTime = date.toISOString();
      endDateTime = createEndOfDateRange(date).toISOString();
    }
    try {
      if (auth.isTokenInvalid()) {
        console.debug('Token was evaluated as invalid, needs to be re-fetched before request will be executed.');
        await auth.forceUpdateToken();
      }

      const response = await exportSchedule(scheduleId, auth.keycloak.token!, tenant, restPath, {
        endDateTime,
        startDateTime,
        offset: timezone,
      });
      if (response.ok) {
        await downloadBlobFile(response);
        toast.success(t(`response.success.scheduling.export.${type}`));
      } else if (response.status >= 500) {
        toast.error(t(`response.unavailable`));
      } else {
        if (isUnauthorized(response.status)) {
          const refreshTokenSucceed = await auth.forceUpdateToken();
          if (refreshTokenSucceed) {
            console.debug('Token was successfully renewed after 401, trying to re-download file.');
            onClickHandler(type, oneDayExport)();
          }

          return;
        }

        const message = await response.json();
        toast.warning(t(`response.${message.errorCode}`));
      }
    } finally {
      setDownloadInProgress(false);
    }
  };

  const oneDayExportKey = `date.${exportTypes.oneDay}`;
  const fullScheduleExportKey = `schedule.${exportTypes.fullSchedule}`;

  const exportXLSFileIcon = <Icon kind="export-xls" height={18} color="#00a8e2" />;

  const dropdownProps: IUncontrolledDropdown = {
    className: 'scheduling-header-form__export-dropdown',
    toggle: {
      dataTestId: EXPORT_SCHEDULE_DROPDOWN_TOGGLE,
      children: <Icon kind="export" color="#00a8e2" size={28} />,
      props: {
        color: 'info',
        title: t('schedule.exportSchedule'),
      },
    },
    menu: {
      props: {
        right: true,
        modifiers: {
          offset: {
            enabled: true,
            offset: '0, 8',
          },
        },
      },
      children: (
        <>
          <DropdownItem data-test-id={EXPORT_SCHEDULE_ONE_DAY} onClick={onClickHandler(exportTypes.oneDay, true)}>
            {exportXLSFileIcon}
            <Trans
              i18nKey="schedule.exportSchedulePerType"
              values={{ exportTypeKey: oneDayExportKey }}
              components={[<strong />]}
            />
          </DropdownItem>
          <DropdownItem data-test-id={EXPORT_SCHEDULE_FULL} onClick={onClickHandler(exportTypes.fullSchedule)}>
            {exportXLSFileIcon}
            <Trans
              i18nKey="schedule.exportSchedulePerType"
              values={{ exportTypeKey: fullScheduleExportKey }}
              components={[<strong />]}
            />
          </DropdownItem>
        </>
      ),
    },
  };

  return (
    <LoadingOverlay loading={downloadInProgress}>
      <UncontrolledDropdown {...dropdownProps} />
    </LoadingOverlay>
  );
};

export const ExportSchedule = compose(withTranslation())(ExportScheduleComponent);
